<script setup lang="ts">
  // Reporting (accounting) categories page (§3 plan). Default state is
  // mirror mode (reports group by menu categories); "Personalizar" opens the
  // managed mode: a 2-level tree editor plus the menu-category mapping list.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import CategoriesService from '@/api/services/categories.service';
  import ReportingCategoriesService from '@/api/services/reporting-categories.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import V2ReportPage from '@/components/v2/stats/V2ReportPage.vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { ICategory } from '@/interfaces/ICategory';
  import { IReportingCategory } from '@/interfaces/IReportingCategory';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const ui = useUi();
  const modals = useModal();
  const restaurantStore = useRestaurantStore();
  const categoriesService = new CategoriesService();
  const proxy = getCurrentInstance()!.proxy;

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  //#region Loading

  const reportingCategories = ref<IReportingCategory[] | null>(null);
  const menuCategories = ref<ICategory[] | null>(null);
  const loading = ref(false);

  // Guards out-of-order responses on rapid restaurant switches.
  let requestGeneration = 0;

  async function reload() {
    const restaurant = restaurantStore.restaurant;
    if (!restaurant) return;

    const generation = ++requestGeneration;
    loading.value = true;
    try {
      const [reporting, menu] = await Promise.all([
        ReportingCategoriesService.getAll(restaurant.id),
        CategoriesService.getAll({ restaurantId: restaurant.id }),
      ]);
      if (generation !== requestGeneration) return;
      reportingCategories.value = reporting;
      menuCategories.value = menu;
    } catch (error) {
      if (generation !== requestGeneration) return;
      ui.serverError(error, i18nT('reportingCategories.loadError'));
    } finally {
      if (generation === requestGeneration) loading.value = false;
    }
  }

  watch(() => restaurantStore.restaurant?.id ?? null, reload, {
    immediate: true,
  });

  //#endregion

  //#region Mode

  /** Managed once any category exists, or after the "Personalizar" CTA. */
  const forceManaged = ref(false);
  const managed = computed(
    () => forceManaged.value || (reportingCategories.value?.length ?? 0) > 0
  );

  //#endregion

  //#region Tree

  function sortSiblings(rows: IReportingCategory[]): IReportingCategory[] {
    return [...rows].sort((a, b) => a.index - b.index || a.id - b.id);
  }

  const roots = computed(() =>
    sortSiblings(
      (reportingCategories.value ?? []).filter((row) => row.parentId === null)
    )
  );

  function childrenOf(parentId: number): IReportingCategory[] {
    return sortSiblings(
      (reportingCategories.value ?? []).filter(
        (row) => row.parentId === parentId
      )
    );
  }

  /** Mapping targets: only leaves (parents with children are ambiguous). */
  const leafOptions = computed(() => {
    const all = reportingCategories.value ?? [];
    const byId = new Map(all.map((row) => [row.id, row]));
    return all
      .filter((row) => !all.some((other) => other.parentId === row.id))
      .map((row) => ({
        id: row.id,
        label: row.parentId
          ? `${byId.get(row.parentId)?.name ?? ''} › ${row.name}`
          : row.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  //#endregion

  //#region Tree mutations

  const saving = ref(false);
  const newRootName = ref('');
  const addingChildOf = ref<number | null>(null);
  const newChildName = ref('');
  const editingId = ref<number | null>(null);
  const editingName = ref('');

  async function runMutation(action: () => Promise<void>, errorKey: string) {
    if (saving.value) return;
    saving.value = true;
    try {
      await action();
      await reload();
    } catch (error) {
      ui.serverError(error, i18nT(errorKey));
    } finally {
      saving.value = false;
    }
  }

  async function createRoot() {
    const restaurant = restaurantStore.restaurant;
    const name = newRootName.value.trim();
    if (!restaurant || !name) return;
    await runMutation(async () => {
      await ReportingCategoriesService.create(restaurant.id, {
        name,
        index: roots.value.length,
      });
      newRootName.value = '';
    }, 'reportingCategories.createError');
  }

  function startAddChild(parent: IReportingCategory) {
    addingChildOf.value = parent.id;
    newChildName.value = '';
  }

  async function createChild(parent: IReportingCategory) {
    const restaurant = restaurantStore.restaurant;
    const name = newChildName.value.trim();
    if (!restaurant || !name) return;
    await runMutation(async () => {
      await ReportingCategoriesService.create(restaurant.id, {
        name,
        parentId: parent.id,
        index: childrenOf(parent.id).length,
      });
      addingChildOf.value = null;
      newChildName.value = '';
    }, 'reportingCategories.createError');
  }

  function startRename(row: IReportingCategory) {
    editingId.value = row.id;
    editingName.value = row.name;
  }

  async function saveRename(row: IReportingCategory) {
    const restaurant = restaurantStore.restaurant;
    const name = editingName.value.trim();
    if (!restaurant) return;
    if (!name || name === row.name) {
      editingId.value = null;
      return;
    }
    await runMutation(async () => {
      await ReportingCategoriesService.update(restaurant.id, row.id, { name });
      editingId.value = null;
    }, 'reportingCategories.renameError');
  }

  async function move(row: IReportingCategory, direction: -1 | 1) {
    const restaurant = restaurantStore.restaurant;
    if (!restaurant) return;
    const siblings =
      row.parentId === null ? roots.value : childrenOf(row.parentId);
    const position = siblings.findIndex((sibling) => sibling.id === row.id);
    const target = position + direction;
    if (target < 0 || target >= siblings.length) return;

    // Normalize to sequential indexes with the node moved, then persist only
    // the rows whose index actually changed.
    const reordered = [...siblings];
    reordered.splice(position, 1);
    reordered.splice(target, 0, row);
    await runMutation(async () => {
      const updates = reordered
        .map((sibling, index) => ({ sibling, index }))
        .filter(({ sibling, index }) => sibling.index !== index);
      await Promise.all(
        updates.map(({ sibling, index }) =>
          ReportingCategoriesService.update(restaurant.id, sibling.id, {
            index,
          })
        )
      );
    }, 'reportingCategories.reorderError');
  }

  async function archive(row: IReportingCategory) {
    const restaurant = restaurantStore.restaurant;
    if (!restaurant) return;
    const hasChildren = childrenOf(row.id).length > 0;
    const confirmed = await modals.confirm({
      title: i18nT('reportingCategories.archiveTitle'),
      message: i18nT(
        hasChildren
          ? 'reportingCategories.archiveMessageWithChildren'
          : 'reportingCategories.archiveMessage',
        { name: row.name }
      ),
      acceptText: i18nT('reportingCategories.archiveAccept'),
      cancelText: i18nT('cancel'),
      acceptColor: 'error',
    });
    if (!confirmed) return;
    await runMutation(async () => {
      await ReportingCategoriesService.remove(restaurant.id, row.id);
    }, 'reportingCategories.archiveError');
  }

  //#endregion

  //#region Menu-category mappings

  const savingMappingIds = ref<Set<number>>(new Set());

  const sortedMenuCategories = computed(() =>
    [...(menuCategories.value ?? [])].sort(
      (a, b) => a.index - b.index || a.id - b.id
    )
  );

  async function onMappingChange(category: ICategory, event: Event) {
    const restaurant = restaurantStore.restaurant;
    if (!restaurant) return;
    const raw = (event.target as HTMLSelectElement).value;
    const newValue = raw === '' ? null : Number(raw);
    const previous = category.reportingCategoryId ?? null;
    if (newValue === previous) return;

    savingMappingIds.value = new Set(savingMappingIds.value).add(category.id);
    try {
      await categoriesService.update({
        ...category,
        reportingCategoryId: newValue,
      });
      category.reportingCategoryId = newValue;
      ui.showToast(AlertType.Success, i18nT('reportingCategories.mappingSaved'));
    } catch (error) {
      ui.serverError(error, i18nT('reportingCategories.mappingError'));
    } finally {
      const next = new Set(savingMappingIds.value);
      next.delete(category.id);
      savingMappingIds.value = next;
    }
  }

  //#endregion
</script>

<template>
  <V2ReportPage :title="String($t('reportingCategories.title'))">
    <!-- Loading -->
    <div v-if="loading && reportingCategories === null" class="page-skeleton">
      <div v-for="n in 2" :key="n" class="skeleton-card" />
    </div>

    <!-- Mirror mode -->
    <div v-else-if="!managed" class="mirror-card">
      <PosUiEmptyState
        icon="shape-outline"
        :title="String($t('reportingCategories.mirrorTitle'))"
        :description="String($t('reportingCategories.mirrorText'))"
      >
        <template #action>
          <PosUiButton
            color="primary"
            size="md"
            icon="pencil-outline"
            @click="forceManaged = true"
          >
            {{ $t('reportingCategories.customize') }}
          </PosUiButton>
        </template>
      </PosUiEmptyState>
    </div>

    <!-- Managed mode -->
    <div v-else class="managed-grid">
      <!-- Tree editor -->
      <section class="editor-card">
        <h2 class="card-title">{{ $t('reportingCategories.treeTitle') }}</h2>
        <p class="card-hint">{{ $t('reportingCategories.treeHint') }}</p>

        <div class="create-row">
          <input
            v-model="newRootName"
            class="create-input"
            type="text"
            maxlength="64"
            :placeholder="String($t('reportingCategories.newPlaceholder'))"
            @keyup.enter="createRoot"
          />
          <PosUiButton
            color="primary"
            size="md"
            icon="plus"
            :disabled="!newRootName.trim() || saving"
            @click="createRoot"
          >
            {{ $t('reportingCategories.add') }}
          </PosUiButton>
        </div>

        <PosUiEmptyState
          v-if="!roots.length"
          icon="shape-plus-outline"
          :title="String($t('reportingCategories.emptyTree'))"
        />

        <ul v-else class="tree-list">
          <li v-for="root in roots" :key="root.id" class="tree-node">
            <div class="node-row">
              <template v-if="editingId === root.id">
                <input
                  v-model="editingName"
                  class="rename-input"
                  type="text"
                  maxlength="64"
                  @keyup.enter="saveRename(root)"
                  @blur="saveRename(root)"
                />
              </template>
              <span v-else class="node-name">{{ root.name }}</span>
              <div class="node-actions">
                <PosUiButton
                  color="neutral"
                  variant="ghost"
                  size="sm"
                  icon="pencil-outline"
                  :disabled="saving"
                  @click="startRename(root)"
                />
                <PosUiButton
                  color="neutral"
                  variant="ghost"
                  size="sm"
                  icon="arrow-up"
                  :disabled="saving"
                  @click="move(root, -1)"
                />
                <PosUiButton
                  color="neutral"
                  variant="ghost"
                  size="sm"
                  icon="arrow-down"
                  :disabled="saving"
                  @click="move(root, 1)"
                />
                <PosUiButton
                  color="neutral"
                  variant="ghost"
                  size="sm"
                  icon="plus"
                  :disabled="saving"
                  @click="startAddChild(root)"
                />
                <PosUiButton
                  color="error"
                  variant="ghost"
                  size="sm"
                  icon="archive-outline"
                  :disabled="saving"
                  @click="archive(root)"
                />
              </div>
            </div>

            <ul
              v-if="childrenOf(root.id).length || addingChildOf === root.id"
              class="child-list"
            >
              <li
                v-for="child in childrenOf(root.id)"
                :key="child.id"
                class="tree-node is-child"
              >
                <div class="node-row">
                  <template v-if="editingId === child.id">
                    <input
                      v-model="editingName"
                      class="rename-input"
                      type="text"
                      maxlength="64"
                      @keyup.enter="saveRename(child)"
                      @blur="saveRename(child)"
                    />
                  </template>
                  <span v-else class="node-name">{{ child.name }}</span>
                  <div class="node-actions">
                    <PosUiButton
                      color="neutral"
                      variant="ghost"
                      size="sm"
                      icon="pencil-outline"
                      :disabled="saving"
                      @click="startRename(child)"
                    />
                    <PosUiButton
                      color="neutral"
                      variant="ghost"
                      size="sm"
                      icon="arrow-up"
                      :disabled="saving"
                      @click="move(child, -1)"
                    />
                    <PosUiButton
                      color="neutral"
                      variant="ghost"
                      size="sm"
                      icon="arrow-down"
                      :disabled="saving"
                      @click="move(child, 1)"
                    />
                    <PosUiButton
                      color="error"
                      variant="ghost"
                      size="sm"
                      icon="archive-outline"
                      :disabled="saving"
                      @click="archive(child)"
                    />
                  </div>
                </div>
              </li>

              <li v-if="addingChildOf === root.id" class="tree-node is-child">
                <div class="node-row">
                  <input
                    v-model="newChildName"
                    class="rename-input"
                    type="text"
                    maxlength="64"
                    :placeholder="
                      String($t('reportingCategories.newChildPlaceholder'))
                    "
                    @keyup.enter="createChild(root)"
                  />
                  <div class="node-actions">
                    <PosUiButton
                      color="primary"
                      variant="soft"
                      size="sm"
                      icon="check"
                      :disabled="!newChildName.trim() || saving"
                      @click="createChild(root)"
                    />
                    <PosUiButton
                      color="neutral"
                      variant="ghost"
                      size="sm"
                      icon="close"
                      @click="addingChildOf = null"
                    />
                  </div>
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </section>

      <!-- Menu-category mapping list -->
      <section class="mapping-card">
        <h2 class="card-title">
          {{ $t('reportingCategories.mappingTitle') }}
        </h2>
        <p class="card-hint">{{ $t('reportingCategories.mappingHint') }}</p>

        <PosUiEmptyState
          v-if="!sortedMenuCategories.length"
          icon="silverware-fork-knife"
          :title="String($t('reportingCategories.noMenuCategories'))"
        />

        <ul v-else class="mapping-list">
          <li
            v-for="category in sortedMenuCategories"
            :key="category.id"
            class="mapping-row"
          >
            <span class="mapping-name">{{ category.name }}</span>
            <select
              class="mapping-select"
              :value="category.reportingCategoryId ?? ''"
              :disabled="savingMappingIds.has(category.id)"
              @change="onMappingChange(category, $event)"
            >
              <option value="">
                {{ $t('reportingCategories.mirrorOption') }}
              </option>
              <option
                v-for="option in leafOptions"
                :key="option.id"
                :value="option.id"
              >
                {{ option.label }}
              </option>
            </select>
          </li>
        </ul>

        <p class="mapping-footnote">
          {{ $t('reportingCategories.itemOverrideNote') }}
        </p>
      </section>
    </div>
  </V2ReportPage>
</template>

<style scoped lang="scss">
  .page-skeleton {
    @apply tw-grid tw-gap-4 lg:tw-grid-cols-2;

    .skeleton-card {
      @apply tw-h-72 tw-animate-pulse tw-rounded-xl tw-bg-neutral-200;
    }
  }

  .mirror-card {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
  }

  .managed-grid {
    @apply tw-grid tw-gap-4 lg:tw-grid-cols-2;

    .card-title {
      @apply tw-mb-1 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }

    .card-hint {
      @apply tw-mb-3 tw-text-xs tw-text-neutral-500;
    }

    .editor-card,
    .mapping-card {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4;
    }

    .create-row {
      @apply tw-mb-3 tw-flex tw-items-center;

      .create-input {
        @apply tw-mr-2 tw-h-11 tw-min-w-0 tw-flex-1 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2.5 tw-text-sm tw-text-neutral-900;

        &:focus {
          @apply tw-border-orange-500 tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    .tree-list {
      @apply tw-m-0 tw-list-none tw-p-0;

      .child-list {
        @apply tw-m-0 tw-list-none tw-p-0 tw-pl-6;
      }

      .tree-node {
        @apply tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100;

        &:last-child {
          @apply tw-border-b-0;
        }

        &.is-child {
          .node-row {
            .node-name {
              @apply tw-font-normal tw-text-neutral-600;
            }
          }
        }

        .node-row {
          // min-h-11 needs TW 3.4; this project is on 3.3.
          @apply tw-flex tw-min-h-[44px] tw-items-center tw-justify-between tw-py-1;

          .node-name {
            @apply tw-mr-2 tw-truncate tw-text-sm tw-font-medium tw-text-neutral-900;
          }

          .rename-input {
            @apply tw-mr-2 tw-h-10 tw-min-w-0 tw-flex-1 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2.5 tw-text-sm tw-text-neutral-900;

            &:focus {
              @apply tw-border-orange-500 tw-outline-none tw-ring-2 tw-ring-orange-500/40;
            }
          }

          .node-actions {
            @apply tw-flex tw-flex-shrink-0 tw-items-center;
          }
        }
      }
    }

    .mapping-list {
      @apply tw-m-0 tw-list-none tw-p-0;

      .mapping-row {
        @apply tw-flex tw-items-center tw-justify-between tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-py-1.5;

        &:last-child {
          @apply tw-border-b-0;
        }

        .mapping-name {
          @apply tw-mr-3 tw-min-w-0 tw-truncate tw-text-sm tw-text-neutral-700;
        }

        .mapping-select {
          @apply tw-h-11 tw-max-w-[60%] tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2 tw-text-sm tw-text-neutral-900;

          &:focus {
            @apply tw-border-orange-500 tw-outline-none tw-ring-2 tw-ring-orange-500/40;
          }

          &:disabled {
            @apply tw-bg-neutral-100 tw-text-neutral-400;
          }
        }
      }
    }

    .mapping-footnote {
      @apply tw-mb-0 tw-mt-3 tw-text-xs tw-text-neutral-400;
    }
  }
</style>
